import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildingColumns } from '@fortawesome/pro-light-svg-icons';
import {
  NpoProfile,
  NpoSyncLog,
  getNpoProfiles,
  getSyncLogs,
  triggerNpoSync,
  cancelNpoSync,
} from './api';

const PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];
const POLL_INTERVAL_MS = 10000;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  .header-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
  }
  .heading {
    margin: 0;
  }
  .subheading {
    margin: 2px 0 0;
    color: ${COLORS.grayDark};
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 7fr 3fr;
  gap: ${SPACERS.medium};
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  padding: ${SPACERS.medium};
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.05);
`;

const SLIM_INPUT_HEIGHT = 40;

const SearchGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr auto auto;
  gap: ${SPACERS.small};
  align-items: end;
  margin: ${SPACERS.small} 0 ${SPACERS.medium};
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
  .field-label {
    display: block;
    margin-bottom: 4px;
    color: ${COLORS.grayDarkest};
  }
  input {
    padding: 9px 12px;
    font-size: 14px;
    height: ${SLIM_INPUT_HEIGHT}px;
    box-sizing: border-box;
  }
`;

const SlimInput = styled.div`
  input {
    padding: 9px 12px;
    font-size: 14px;
    height: ${SLIM_INPUT_HEIGHT}px;
    box-sizing: border-box;
  }
`;

const SyncEinSection = styled.div`
  margin-top: ${SPACERS.medium};
  .field-label {
    display: block;
    margin-bottom: 4px;
    color: ${COLORS.grayDarkest};
  }
`;

const TableWrapper = styled.div`
  .mono {
    font-family: monospace;
  }
  .pdf-link {
    color: ${COLORS.ctaPrimary};
    text-decoration: none;
    font-weight: 600;
  }
  .pdf-link:hover {
    text-decoration: underline;
  }
`;

const ErrorBanner = styled.div`
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border: 1px solid ${COLORS.critical};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-top: ${SPACERS.small};
  white-space: pre-wrap;
  word-break: break-word;
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${SPACERS.small};
  .pagination-loader {
    display: flex;
    align-items: center;
    color: ${COLORS.grayDark};
  }
  .MuiTablePagination-root {
    overflow: visible;
  }
  .MuiTablePagination-toolbar {
    align-items: center;
    min-height: 44px;
  }
  .MuiTablePagination-selectLabel,
  .MuiTablePagination-displayedRows {
    margin: 0;
    line-height: 1;
  }
  .MuiTablePagination-select {
    display: flex;
    align-items: center;
  }
  .MuiTablePagination-actions {
    display: flex;
    align-items: center;
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.tiny};
  background: ${COLORS.ctaPrimaryBackground};
  color: ${COLORS.ctaPrimary};
  border: 1px solid ${COLORS.ctaPrimarySubdued};
  border-radius: 8px;
  padding: ${SPACERS.tiny} ${SPACERS.small};
  margin-bottom: ${SPACERS.small};
  font-size: 14px;
  font-weight: 600;
`;

const StatusPill = styled.span<{ tone: 'running' | 'success' | 'error' }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${p =>
    p.tone === 'error'
      ? COLORS.critical
      : p.tone === 'success'
      ? COLORS.success
      : COLORS.warningDark};
  background: ${p =>
    p.tone === 'error'
      ? COLORS.criticalBackground
      : p.tone === 'success'
      ? COLORS.successBackground
      : COLORS.warningBackground};
`;

const TypePill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${COLORS.grayDarkest};
  background: ${COLORS.greyLight};
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${COLORS.greyLight};
  overflow: hidden;
  margin: ${SPACERS.tiny} 0;
`;

const ProgressFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${p => p.pct}%;
  background: ${COLORS.ctaPrimary};
  transition: width 0.4s ease;
`;

const SyncSection = styled.div`
  margin-bottom: ${SPACERS.medium};
  .kv {
    display: flex;
    justify-content: space-between;
    gap: ${SPACERS.small};
    padding: 3px 0;
    color: ${COLORS.grayDarkest};
  }
  .kv .k {
    color: ${COLORS.grayDark};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatGqlError = (err: any): string => {
  const gqlMessage = err?.graphQLErrors?.[0]?.message;
  if (gqlMessage) return gqlMessage;
  if (err?.networkError?.message) return err.networkError.message;
  return err?.message || 'Something went wrong. Please try again.';
};

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const statusTone = (status: string): 'running' | 'success' | 'error' => {
  const normalized = (status || '').toLowerCase();
  return normalized === 'success'
    ? 'success'
    : normalized === 'error'
    ? 'error'
    : 'running';
};

const OrganizationExplorer: React.FC = () => {
  const client = useClient();
  const [search, setSearch] = useState('');
  const [ein, setEin] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedEin, setAppliedEin] = useState('');
  const [rows, setRows] = useState<NpoProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getNpoProfiles(client, {
        page,
        perPage,
        search: appliedSearch.trim() || null,
        ein: appliedEin.trim() || null,
      });
      setRows(result.nodes);
      setTotal(result.totalCount);
    } catch (err) {
      setError(formatGqlError(err));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [client, page, perPage, appliedSearch, appliedEin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyFilters = () => {
    setPage(0);
    setAppliedSearch(search);
    setAppliedEin(ein);
  };

  const clearFilters = () => {
    setSearch('');
    setEin('');
    setPage(0);
    setAppliedSearch('');
    setAppliedEin('');
  };

  return (
    <Card>
      <TrueImpactText variant="heading-subheading">
        Organization Explorer
      </TrueImpactText>
      <TrueImpactText
        variant="body-small"
        color="grayDark"
        style={{ display: 'block', marginTop: 4 }}
      >
        Browse nonprofit records. Search by EIN to fetch an
        organization on demand if it is not stored yet.
      </TrueImpactText>

      <SearchGrid>
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            Search
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="Name, city, state, ZIP…"
            value={search}
            onChange={v => setSearch(v)}
          />
        </div>
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            EIN
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="e.g. 142007220"
            value={ein}
            onChange={v => setEin(v)}
          />
        </div>
        <TrueImpactButton
          variant="primary-small"
          onClick={applyFilters}
          style={{ height: SLIM_INPUT_HEIGHT }}
        >
          Apply
        </TrueImpactButton>
        <TrueImpactButton
          variant="secondary-small"
          onClick={clearFilters}
          style={{ height: SLIM_INPUT_HEIGHT }}
        >
          Clear
        </TrueImpactButton>
      </SearchGrid>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {loading && rows.length === 0 ? (
        <TrueImpactLoading />
      ) : (
        <TableWrapper>
          <TableContainer
            style={{ opacity: loading ? 0.6 : 1, transition: '0.2s' }}
          >
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>EIN</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>TI Account</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Latest Tax Year</TableCell>
                  <TableCell>990 PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No organizations found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(row => {
                    const location = [row.city, row.state]
                      .filter(Boolean)
                      .join(', ');
                    const pdfUrl = row.financialSummary?.pdfUrl990;
                    return (
                      <TableRow key={row.dbId} hover>
                        <TableCell title={row.name || ''}>
                          {row.name || '—'}
                        </TableCell>
                        <TableCell className="mono">{row.ein}</TableCell>
                        <TableCell>{formatDate(row.ppUpdatedAt)}</TableCell>
                        <TableCell className="mono">
                          {row.accountId ?? '—'}
                        </TableCell>
                        <TableCell>{location || '—'}</TableCell>
                        <TableCell>
                          {row.financialSummary?.taxPrdYr ?? '—'}
                        </TableCell>
                        <TableCell>
                          {pdfUrl ? (
                            <a
                              className="pdf-link"
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View 990
                            </a>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </MuiTable>
          </TableContainer>

          <PaginationBar>
            {loading && (
              <span className="pagination-loader">
                <TrueImpactLoading singleLine marginTop={0} marginBottom={0} />
              </span>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onRowsPerPageChange={e => {
                setPage(0);
                setPerPage(parseInt(e.target.value, 10));
              }}
            />
          </PaginationBar>
        </TableWrapper>
      )}
    </Card>
  );
};

const DataSync: React.FC = () => {
  const client = useClient();
  const [latest, setLatest] = useState<NpoSyncLog | null>(null);
  const [einInput, setEinInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadLatest = useCallback(async () => {
    if (!client) return;
    try {
      const logs = await getSyncLogs(client, { last: 1 });
      const log = logs[0] || null;
      setLatest(log);
      // Once the run is no longer in progress, clear any pending cancel state.
      if (log && !log.inProgress) setCancelling(false);
      return log;
    } catch (err) {
      setError(formatGqlError(err));
      return null;
    }
  }, [client]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const log = await loadLatest();
      if (log && !log.inProgress) stopPolling();
    }, POLL_INTERVAL_MS);
  }, [loadLatest, stopPolling]);

  useEffect(() => {
    setLoading(true);
    loadLatest().then(log => {
      setLoading(false);
      if (log?.inProgress) startPolling();
    });
    return stopPolling;
  }, [loadLatest, startPolling, stopPolling]);

  const runSync = async (syncType: 'all' | 'single_ein') => {
    if (!client) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await triggerNpoSync(client, {
        syncType,
        ein: syncType === 'single_ein' ? einInput.trim() : null,
      });
      if (!result?.success) {
        setError('The sync request was not accepted.');
        return;
      }
      if (syncType === 'single_ein') setEinInput('');
      await loadLatest();
      startPolling();
    } catch (err) {
      setError(formatGqlError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelSync = async () => {
    if (!client || !latest) return;
    setCancelling(true);
    setError(null);
    try {
      const result = await cancelNpoSync(client, { logId: latest.dbId });
      if (!result?.success) {
        setError('The cancel request was not accepted.');
        setCancelling(false);
        return;
      }
      // Worker stops at its next checkpoint; keep polling until it finalizes.
      await loadLatest();
      startPolling();
    } catch (err) {
      setError(formatGqlError(err));
      setCancelling(false);
    }
  };

  const isSyncing = !!latest?.inProgress;
  const progressPct =
    latest && latest.itemsTotal > 0
      ? Math.min(
          100,
          Math.round(
            ((latest.itemsProcessed +
              latest.itemsSkipped +
              latest.itemsFailed) /
              latest.itemsTotal) *
              100,
          ),
        )
      : 0;

  return (
    <Card>
      <TrueImpactText variant="heading-subheading">Data Sync</TrueImpactText>
      <TrueImpactText
        variant="body-small"
        color="grayDark"
        style={{ display: 'block', marginTop: 4, marginBottom: SPACERS.small }}
      >
        Review the latest sync and trigger an on-demand run.
      </TrueImpactText>

      {submitting && (
        <InfoBanner>
          <TrueImpactLoading singleLine marginTop={0} marginBottom={0} />
          Requesting sync…
        </InfoBanner>
      )}
      {cancelling && (
        <InfoBanner>
          <TrueImpactLoading singleLine marginTop={0} marginBottom={0} />
          Requesting cancellation…
        </InfoBanner>
      )}

      {loading ? (
        <TrueImpactLoading />
      ) : latest ? (
        <SyncSection>
          <div className="kv">
            <span className="k">Status</span>
            <StatusPill tone={statusTone(latest.status)}>
              {latest.status}
            </StatusPill>
          </div>
          <div className="kv">
            <span className="k">Type</span>
            <TypePill>{latest.syncType}</TypePill>
          </div>
          <div className="kv">
            <span className="k">Started</span>
            <span>{formatDate(latest.startedAt)}</span>
          </div>
          <div className="kv">
            <span className="k">Ended</span>
            <span>{latest.endedAt ? formatDate(latest.endedAt) : '—'}</span>
          </div>
          {latest.triggerEin && (
            <div className="kv">
              <span className="k">Trigger EIN</span>
              <span>{latest.triggerEin}</span>
            </div>
          )}

          {isSyncing && (
            <div style={{ marginTop: SPACERS.small }}>
              <ProgressTrack>
                <ProgressFill pct={progressPct} />
              </ProgressTrack>
              <TrueImpactText variant="body-small" color="grayDark">
                {latest.itemsProcessed + latest.itemsSkipped + latest.itemsFailed}
                {' / '}
                {latest.itemsTotal} processed · {latest.itemsRemaining} remaining
              </TrueImpactText>
              <TrueImpactButton
                variant="secondary-small"
                disabled={cancelling}
                onClick={cancelSync}
                style={{ width: '100%', marginTop: SPACERS.small }}
              >
                {cancelling ? 'Cancelling…' : 'Cancel Sync'}
              </TrueImpactButton>
            </div>
          )}

          <div className="kv" style={{ marginTop: SPACERS.small }}>
            <span className="k">Processed</span>
            <span>{latest.itemsProcessed}</span>
          </div>
          <div className="kv">
            <span className="k">Skipped</span>
            <span>{latest.itemsSkipped}</span>
          </div>
          <div className="kv">
            <span className="k">Failed</span>
            <span>{latest.itemsFailed}</span>
          </div>
          {latest.error && <ErrorBanner>{latest.error}</ErrorBanner>}
        </SyncSection>
      ) : (
        <TrueImpactText
          variant="body-small"
          color="grayDark"
          style={{ display: 'block', marginBottom: SPACERS.small }}
        >
          No sync runs yet.
        </TrueImpactText>
      )}

      {!loading && !isSyncing && (
        <TrueImpactButton
          variant="secondary-small"
          disabled={submitting}
          onClick={() => runSync('all')}
          style={{ width: '100%' }}
        >
          Sync all data
        </TrueImpactButton>
      )}

      <SyncEinSection>
        <TrueImpactText className="field-label" variant="body-small-bold">
          Sync a single EIN
        </TrueImpactText>
        <SlimInput>
          <TrueImpactTextfield
            placeholder="e.g. 142007220"
            value={einInput}
            onChange={v => setEinInput(v)}
          />
        </SlimInput>
        <TrueImpactButton
          variant="secondary-small"
          disabled={submitting || isSyncing || !einInput.trim()}
          onClick={() => runSync('single_ein')}
          style={{ width: '100%', marginTop: SPACERS.small }}
        >
          Sync this EIN
        </TrueImpactButton>
      </SyncEinSection>

      {error && <ErrorBanner>{error}</ErrorBanner>}
    </Card>
  );
};

const IrsManagement: React.FC = () => (
  <PageWrapper>
    <PageHeader>
      <div className="header-icon">
        <FontAwesomeIcon icon={faBuildingColumns} size="lg" />
      </div>
      <div>
        <TrueImpactText variant="heading-page" className="heading">
          IRS Management
        </TrueImpactText>
        <TrueImpactText variant="body-small" className="subheading">
          Browse nonprofit data and run on-demand IRS data syncs.
        </TrueImpactText>
      </div>
    </PageHeader>

    <DashboardGrid>
      <OrganizationExplorer />
      <DataSync />
    </DashboardGrid>
  </PageWrapper>
);

export default IrsManagement;
